import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ChevronUp,
  User,
  Info,
  Heart,
  MessageSquare,
  ExternalLink,
  CheckCircle,
  Sparkles,
  Copy,
  Check,
  Share,
  Briefcase,
  Linkedin,
} from "lucide-react";
import PremiumLeadGate from "./PremiumLeadGate";

const LinkedInCard = React.memo(
  ({ lead, selectedProduct, expandedCard, toggleCard, togglePostCompletion, isPremiumUser = false, onUpgradeClick, leadIndex = 0 }) => {
    const [copied, setCopied] = useState(false);

    // Determine if this lead should be locked (free users only see top N leads)
    const isTopLead = leadIndex < 3; // Show first 3 leads to free users
    const shouldLockLead = !isPremiumUser && !isTopLead;

    // Reset copied state when card changes
    useEffect(() => {
      setCopied(false);
    }, [lead.id]);

    const handleCopy = (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Get the actual text content we want to copy
      const textToCopy =
        lead.details.comment || `Introduce yourself and explain how ${selectedProduct?.name} could help with their specific need.`;

      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard
          .writeText(textToCopy)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          })
          .catch((err) => {
            console.error("Clipboard API error:", err);
            fallbackCopy(textToCopy);
          });
      } else {
        fallbackCopy(textToCopy);
      }
    };

    // Fallback copy method using document.execCommand
    const fallbackCopy = (text) => {
      try {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand("copy");
        document.body.removeChild(textArea);

        if (successful) {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } else {
          alert("Copy failed. Please try manually selecting and copying the text.");
        }
      } catch (err) {
        console.error("Fallback copy failed:", err);
        alert("Copy failed. Please try manually selecting and copying the text.");
      }
    };

    const getScoreBadgeColor = (score) => {
      if (score >= 80) return "bg-green-100 text-green-800 border-green-200";
      if (score >= 60) return "bg-yellow-100 text-yellow-800 border-yellow-200";
      return "bg-red-100 text-red-800 border-red-200";
    };

    // Card click handler that respects premium status
    const handleCardClick = () => {
      if (shouldLockLead) {
        onUpgradeClick();
      } else {
        toggleCard(lead.id);
      }
    };

    // Format LinkedIn post date
    const formatDate = (timestamp) => {
      if (!timestamp) return "";
      try {
        // LinkedIn timestamps are in milliseconds
        const date = new Date(parseInt(timestamp));
        return date.toLocaleDateString();
      } catch (e) {
        return "";
      }
    };

    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 relative ${
          lead.open_insight ? "opacity-70" : ""
        }`}
      >
        {/* Premium Gate Overlay for locked leads */}
        {shouldLockLead && <PremiumLeadGate onClick={onUpgradeClick} />}

        <div className="p-5" onClick={handleCardClick}>
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                {lead.details.authorType === "Company" ? (
                  <Briefcase className="w-5 h-5 text-purple-600" />
                ) : (
                  <User className="w-5 h-5 text-purple-600" />
                )}
              </div>
              <div>
                <h3 className="font-medium text-gray-800">{lead.details.user_handle || "LinkedIn User"}</h3>
                <p className="text-xs text-gray-500">{lead.details.authorType || "Professional"}</p>
                <p className="text-xs text-gray-400">
                  {formatDate(lead.details.postedAtTimestamp) ||
                    lead.details.timeSincePosted ||
                    new Date(lead.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <span className={`text-sm font-bold rounded-full px-2.5 py-1 border ${getScoreBadgeColor(lead.details.lead_quality_score)}`}>
                {lead.details.lead_quality_score || 0}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (!shouldLockLead) toggleCard(lead.id);
                }}
                className="ml-2 p-1 hover:bg-gray-100 rounded-full"
              >
                {expandedCard === lead.id ? (
                  <ChevronUp size={20} className="text-gray-500" />
                ) : (
                  <ChevronDown size={20} className="text-gray-500" />
                )}
              </button>
            </div>
          </div>

          {/* Post title if available */}
          {lead.details.title && <h4 className="font-medium text-gray-800 mb-2">{lead.details.title}</h4>}

          <p className="text-gray-800 mb-4 line-clamp-3">{lead.details.post_excerpt || lead.details.text || "No content available"}</p>

          <div className="flex items-center text-gray-500 text-sm mb-4">
            <div className="flex items-center mr-4">
              <Heart className="w-4 h-4 mr-1" />
              <span>{lead.details.numLikes || lead.details.like_count || 0}</span>
            </div>
            <div className="flex items-center mr-4">
              <Share className="w-4 h-4 mr-1" />
              <span>{lead.details.numShares || 0}</span>
            </div>
            <div className="flex items-center">
              <MessageSquare className="w-4 h-4 mr-1" />
              <span>{lead.details.numComments || lead.details.comment_count || 0}</span>
            </div>
          </div>

          <div className="flex justify-between items-center text-sm">
            <div className="text-purple-600 flex items-center">
              <Info className="w-4 h-4 mr-1" />
              <span>{lead.details.lead_score_explanation || "Potential lead for your product"}</span>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {expandedCard === lead.id && !lead.open_insight && !shouldLockLead && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="border-t border-gray-200"
            >
              <div className="p-5">
                <div className="bg-purple-50 rounded-lg p-4 mb-4 border border-purple-100" id={`suggestion-${lead.id}`}>
                  <h4 className="font-medium mb-2 text-purple-800 flex items-center">
                    <Sparkles size={18} className="mr-2 text-purple-500" />
                    Suggested Approach
                  </h4>
                  <p className="text-gray-700 text-sm">
                    {lead.details.comment ||
                      `Introduce yourself and explain how ${selectedProduct?.name} could help with their specific need.`}
                  </p>
                  <div className="flex justify-end mt-3">
                    <button onClick={handleCopy} className="text-xs flex items-center space-x-1 text-purple-600 hover:text-purple-700">
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                      <span className="ml-1">{copied ? "Copied!" : "Copy text"}</span>
                    </button>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <button
                    className="text-sm bg-white text-purple-600 border border-purple-200 hover:bg-purple-50 px-3 py-2 rounded-lg flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(lead.details.url || lead.details.post_url, "_blank");
                    }}
                  >
                    <ExternalLink size={14} className="mr-1" />
                    View on LinkedIn
                  </button>

                  <button
                    className="text-sm bg-purple-600 text-white hover:bg-purple-700 px-3 py-2 rounded-lg flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePostCompletion(lead.id);
                    }}
                  >
                    <CheckCircle size={14} className="mr-1" />
                    Mark as Engaged
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }
);

export default LinkedInCard;
